import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { StyledMenu } from '@ui/components/common/Menu';
import ReassignVehicleDialog from '../ReassignVehicleDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getFamilyFestAttendeesByPickupLocationIdAsync, unassignFamilyFestAttendeeFromRouteVehicleAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { assignAttendeeToRouteVehicleAsync } from '@application/features/eventVehicle/eventVehicleThunk';
import { getFamilyFestPickupLocationByIdAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { AttendeeStatusEnum } from '@application/enums/attendee-status-enum';
import { toast } from 'react-toastify';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
const LocationAttendeesTab = ({ filterOpen, onFilterOpenChange, searchValue, locationId, selectedRows, onSelectionChange, bulkAssignVehicleOpen, onBulkAssignVehicleOpenChange, refreshKey, resetFiltersKey, onFiltersChange }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { attendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [selectedAttendeeForVehicle, setSelectedAttendeeForVehicle] = useState(null);
    const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
    const [vehicleDialogMode, setVehicleDialogMode] = useState('assign');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filters, setFilters] = useState({});
    const [reloadKey, setReloadKey] = useState(0);
    // Cleared from the parent's filter "X" button. Updaters return the same reference when
    // nothing changes, so the initial run does not trigger an extra fetch.
    useEffect(() => {
        setFilters((prev) => (Object.keys(prev).length > 0 ? {} : prev));
        setPaginationModel((prev) => (prev.page > 0 ? { ...prev, page: 0 } : prev));
    }, [resetFiltersKey]);
    useEffect(() => {
        if (!locationId)
            return;
        const sort = sortModel[0];
        const sortFieldMap = {
            company: 'companyname',
            type: 'attendeetype',
            registered: 'createdon'
        };
        dispatch(getFamilyFestAttendeesByPickupLocationIdAsync({
            pickupLocationId: String(locationId),
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue || undefined,
            name: filters.name,
            vehicleNumber: filters.vehicleNumber,
            routeName: filters.routeName,
            companyName: filters.company,
            attendeeType: filters.type,
            primaryAttendeeName: filters.primaryPerson,
            registeredDate: filters.registered,
            status: filters.status,
            sortBy: sort ? (sortFieldMap[sort.field] ?? sort.field) : undefined,
            sortOrder: sort?.sort ?? 'desc'
        }));
    }, [dispatch, locationId, paginationModel.page, paginationModel.pageSize, searchValue, filters, sortModel, selectedCompanyIds, refreshKey, reloadKey]);
    const refreshAttendees = useCallback(() => {
        if (!locationId)
            return;
        dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
        // Re-runs the list fetch effect so the active filters, search and sort are kept.
        setReloadKey((key) => key + 1);
    }, [dispatch, locationId]);
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    }, []);
    const handleSendToUnassigned = useCallback(() => {
        handleMenuClose();
        if (!menuRow)
            return;
        dispatch(unassignFamilyFestAttendeeFromRouteVehicleAsync([menuRow.id]))
            .unwrap()
            .then(() => {
            toast.success(t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully'));
            refreshAttendees();
        })
            .catch((error) => {
            toast.error(error?.error || t('events.eventDetails.transportTab.attendeeUnassignFailed'));
        });
    }, [handleMenuClose, menuRow, dispatch, refreshAttendees, t]);
    const handleAssignVehicle = useCallback(() => {
        if (menuRow)
            setSelectedAttendeeForVehicle(menuRow);
        handleMenuClose();
        setVehicleDialogMode('assign');
        setVehicleDialogOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleReassignVehicle = useCallback(() => {
        if (menuRow)
            setSelectedAttendeeForVehicle(menuRow);
        handleMenuClose();
        setVehicleDialogMode('reassign');
        setVehicleDialogOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleVehicleConfirm = useCallback((vehicle) => {
        if (!selectedAttendeeForVehicle)
            return;
        const successKey = vehicleDialogMode === 'assign'
            ? 'events.eventDetails.transportTab.attendeeAssignedSuccessfully'
            : 'events.eventDetails.transportTab.attendeeReassignedSuccessfully';
        const failedKey = vehicleDialogMode === 'assign'
            ? 'events.eventDetails.transportTab.attendeeAssignFailed'
            : 'events.eventDetails.transportTab.attendeeReassignFailed';
        dispatch(assignAttendeeToRouteVehicleAsync({
            assignmentIds: [selectedAttendeeForVehicle.id],
            routeVehicleId: vehicle.id
        }))
            .unwrap()
            .then(() => {
            toast.success(t(successKey));
            refreshAttendees();
            setSelectedAttendeeForVehicle(null);
            setVehicleDialogOpen(false);
        })
            .catch((error) => {
            toast.error(error?.error || t(failedKey));
        });
    }, [selectedAttendeeForVehicle, vehicleDialogMode, dispatch, refreshAttendees, t]);
    const rowMenuActions = useMemo(() => {
        if (menuRow?.status === AttendeeStatusEnum.Assigned) {
            return [
                { text: t('events.eventDetails.transportTab.sendToUnassigned'), to: handleSendToUnassigned, group: true },
                { text: t('events.eventDetails.transportTab.reassignVehicle'), to: handleReassignVehicle, group: true }
            ];
        }
        return [
            { text: t('events.eventDetails.transportTab.assignVehicle'), to: handleAssignVehicle, group: true }
        ];
    }, [t, menuRow, handleSendToUnassigned, handleReassignVehicle, handleAssignVehicle]);
    const columns = useMemo(() => [
        {
            field: 'name',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 150,
            filterField: 'name',
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name || '—' }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'vehicleNumber',
            headerName: t('events.eventDetails.transportTab.vehicleNumber'),
            flex: 0.9,
            minWidth: 130,
            filterField: 'vehicleNumber',
            renderCell: (params) => params.row.vehicleNumber || '—'
        },
        {
            field: 'company',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 140,
            filterField: 'company',
            renderCell: (params) => params.row.companyName || '—'
        },
        {
            field: 'type',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 110,
            filterField: 'type',
            renderCell: (params) => params.row.attendeeType || '—'
        },
        {
            field: 'primaryPerson',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.primaryPerson'),
            flex: 1,
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => params.row.primaryAttendeeName || '-'
        },
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.2,
            minWidth: 160,
            filterField: 'registered',
            renderCell: (params) => formatRegisteredDateOnly(params.row.createdDate)
        },
        {
            field: 'status',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.status'),
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isAssigned = params.row.status === AttendeeStatusEnum.Assigned;
                const chipProps = isAssigned
                    ? {
                        label: t('events.eventDetails.transportTab.statusAssigned'),
                        color: 'success',
                        variant: 'soft'
                    }
                    : {
                        label: t('events.eventDetails.transportTab.statusUnassigned'),
                        color: 'default',
                        variant: 'soft'
                    };
                return _jsx(Chip, { size: 'small', ...chipProps });
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.35,
            minWidth: 70,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', width: '100%', children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRow(params.row);
                    }, children: _jsx(MoreHorizIcon, {}) }) }))
        }
    ], [t]);
    const filterOptions = useMemo(() => ({
        type: [{ value: 'Primary', label: t('events.eventDetails.transportTab.typePrimary') }],
        status: [
            { value: AttendeeStatusEnum.Assigned, label: t('events.eventDetails.transportTab.statusAssigned') },
            { value: AttendeeStatusEnum.Unassigned, label: t('events.eventDetails.transportTab.statusUnassigned') }
        ]
    }), [t]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { showHeaderControls: false, showNewHeaderControls: true, filterOpen: filterOpen, onFilterOpenChange: onFilterOpenChange, columns: columns, filterOptions: filterOptions, resetFiltersKey: resetFiltersKey, rows: attendees?.items || [], isMultiple: true, onSelectionChange: (model) => onSelectionChange(model), rowSelectionModel: selectedRows, paginationModel: paginationModel, onPaginationModelChange: (newModel) => setPaginationModel(newModel), onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    onFiltersChange?.(newFilters);
                    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                }, paginationMode: 'server', rowCount: attendees?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [10, 25, 50, 100], hideFooter: false, sx: { height: 'calc(100dvh - 464px)' } }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(ReassignVehicleDialog, { open: vehicleDialogOpen, onClose: () => {
                    setSelectedAttendeeForVehicle(null);
                    setVehicleDialogOpen(false);
                }, onConfirm: handleVehicleConfirm, title: t(vehicleDialogMode === 'assign'
                    ? 'events.eventDetails.transportTab.assignVehicle'
                    : 'events.eventDetails.transportTab.reassignVehicle'), isFamilyFest: true }), _jsx(ReassignVehicleDialog, { open: bulkAssignVehicleOpen, onClose: () => onBulkAssignVehicleOpenChange(false), title: t('events.eventDetails.transportTab.assignVehicle'), attendeeIds: selectedRows.length > 0 ? selectedRows : undefined, locationId: locationId, isFamilyFest: true, onSuccess: () => {
                    onSelectionChange([]);
                    refreshAttendees();
                } })] }));
};
export default LocationAttendeesTab;
